<template>
  <NuxtLink :to="toPath" :target="linkTarget" class="search-result-item">
    <div v-if="data.type == 'city' || isTopCity" class="icon">
      <IconLocationOutline />
    </div>
    <NuxtImg loading="lazy"
      v-no-right-click
      v-else
      :src="data.image_url ?? data.thumbnail"
      :alt="data?.title"
      class="w-[38px] h-[38px] rounded object-cover me-2.5"
    />
    <div class="content">
      <h6>
        {{ isTopCity ? data.name : data?.title }}
      </h6>
      <span>
        {{ data.type == "product" || searchInCity ? productText : countryText }}
      </span>
    </div>
  </NuxtLink>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from "vue";

const { t } = useI18n();
const localePath = useLocalePath();

const props = defineProps({
  data: { type: Object, required: true },
  isTopCity: { type: Boolean, default: false },
  searchInCity: { type: Boolean, default: false },
});

const config = useRuntimeConfig();
const isDesktop = ref<boolean>(false);
const layout = config.public.layout;

const countryText = computed(() => {
  return `${t("searchBox.cityIn")} ${props.data.country_name}`;
});

const productText = computed(() => {
  return props.searchInCity
    ? `${t("activityIn")} ${props.data.cities[0].name}, ${props.data.cities[0].country.name}`
    : `${t("activityIn")} ${props.data.city_name}, ${props.data.country_name}`;
});

const toPath = computed(() => {
  let path;
  if (props.data.type == "product") {
    if (layout.type.includes("supplier")) {
      path = localePath(`/experiences/${props.data.slug}`);
    } else {
      path = localePath(
        `/things-to-do-in-${props.data.city_slug}/${props.data.slug}`
      );
    }
  } else {
    if (props.searchInCity) {
      if (layout.type.includes("supplier")) {
        path = localePath(`/experiences/${props.data.slug}`);
      } else {
        if (props.data.city_slug) {
          path = localePath(
            `/things-to-do-in-${props.data.city_slug}/${props.data.slug}`
          );
        } else {
          path = localePath(
            `/things-to-do-in-${props.data.cities[0].slug}/${props.data.slug}`
          );
        }
      }
    } else {
      path = localePath(`/things-to-do-in-${props.data.slug}`);
    }
  }
  return path;
});

const linkTarget = computed(() => {
  return isDesktop.value && props.data.type === "product" ? "_blank" : "_self";
});

onMounted(() => {
  isDesktop.value = window.innerWidth >= 1024;
});
</script>

<style scoped>
.search-result-item {
  @apply flex items-center p-3 my-1.5 lg:my-0.5 mx-2 rounded-[5px]
  lg:bg-white transition-colors hover:bg-gray-5 cursor-pointer;
}
.search-result-item .icon {
  @apply me-2.5 p-2.5 rounded-full bg-gray-5 transition-colors;
}
.search-result-item:hover .icon {
  @apply bg-primary-2;
}
.search-result-item .icon svg {
  @apply text-[16px] lg:text-[18px] text-gray-1 !mb-0 transition-colors;
}
.search-result-item:hover .icon svg {
  @apply text-white;
}
.search-result-item .content {
  @apply flex flex-col justify-start text-gray-1 text-start;
}
.search-result-item .content h6 {
  @apply text-mobile-heading-6 lg:text-heading-6 text-primary-1 mb-1.5 lg:mb-1 text-start;
}
.search-result-item .content span {
  @apply text-gray-1 text-start text-[11px] font-normal leading-3 lg:leading-[14px];
}
</style>
